import { FormEvent, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Star, X } from "lucide-react";
import { instructorApi } from "../api/instructor";
import { useUiStore } from "../store/ui";
import { BilanCompetency } from "../types";

const LEVELS: Array<{ key: string; label: string; tone: string }> = [
  {
    key: "acquis",
    label: "Acquis",
    tone: "bg-green-100 text-green-700 border-green-300",
  },
  {
    key: "en_cours",
    label: "En cours",
    tone: "bg-amber-100 text-amber-700 border-amber-300",
  },
  {
    key: "a_revoir",
    label: "À revoir",
    tone: "bg-red-100 text-red-700 border-red-300",
  },
];

const SUGGESTED = [
  "Démarrage / arrêt",
  "Allure et trajectoire",
  "Créneau",
  "Rond-point",
  "Vérifications intérieures",
];

export default function BilanModal() {
  const target = useUiStore((s) => s.bilanTarget);
  const close = useUiStore((s) => s.closeBilan);
  const queryClient = useQueryClient();

  const [summary, setSummary] = useState("");
  const [chosenLesson, setChosenLesson] = useState("");
  const lessons = useQuery({
    queryKey: ["student-lessons", target?.candidateId],
    queryFn: () => instructorApi.getStudentLessons(target!.candidateId),
    enabled: !!target && !target.lessonId,
  });
  const [nextObjective, setNextObjective] = useState("");
  const [secretaryAlert, setSecretaryAlert] = useState("");
  const [examReason, setExamReason] = useState("");
  const [command, setCommand] = useState<{ hash: string; id: string } | null>(
    null,
  );
  const [rating, setRating] = useState<number>(0);
  const [readyForExam, setReadyForExam] = useState(false);
  const [competencies, setCompetencies] = useState<BilanCompetency[]>([]);

  const reset = () => {
    setSummary("");
    setChosenLesson("");
    setNextObjective("");
    setSecretaryAlert("");
    setExamReason("");
    setCommand(null);
    setRating(0);
    setReadyForExam(false);
    setCompetencies([]);
  };

  const mutation = useMutation({
    mutationFn: instructorApi.createBilan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["today"] });
      queryClient.invalidateQueries({ queryKey: ["planning"] });
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["student-lessons"] });
      queryClient.invalidateQueries({ queryKey: ["student"] });
      reset();
      close();
    },
  });

  const resetMutation = mutation.reset;
  useEffect(() => {
    reset();
    resetMutation();
  }, [target?.candidateId, target?.lessonId, resetMutation]);

  if (!target) return null;

  const setLevel = (label: string, level: string) => {
    setCompetencies((prev) => {
      const others = prev.filter((c) => c.label !== label);
      return [...others, { label, level }];
    });
  };
  const levelOf = (label: string) =>
    competencies.find((c) => c.label === label)?.level ?? null;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!summary.trim()) return;
    const payload = {
      candidateId: target.candidateId,
      lessonId: target.lessonId || chosenLesson,
      summary: summary.trim(),
      rating: rating || undefined,
      readyForExam,
      competencies,
      nextObjective,
      secretaryAlert,
      examReason,
    };
    const hash = JSON.stringify(payload),
      requestId = command?.hash === hash ? command.id : crypto.randomUUID();
    setCommand({ hash, id: requestId });
    mutation.mutate({ ...payload, requestId });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h2 className="text-slate-900 text-lg font-semibold">
              Bilan de leçon
            </h2>
            <p className="text-xs text-slate-500">{target.candidateName}</p>
          </div>
          <button
            onClick={close}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="px-6 py-5 space-y-5">
          {!target.lessonId && (
            <label className="block text-sm">
              Leçon évaluée
              <select
                required
                value={chosenLesson}
                onChange={(e) => setChosenLesson(e.target.value)}
                className="w-full rounded-lg border p-3"
              >
                <option value="">Choisir une leçon</option>
                {lessons.data?.map((l) => (
                  <option key={l.id} value={l.id}>
                    {new Date(l.startsAt).toLocaleString("fr-FR")} ·{" "}
                    {l.instructorName}
                  </option>
                ))}
              </select>
              {lessons.isError && (
                <button type="button" onClick={() => void lessons.refetch()}>
                  Réessayer
                </button>
              )}
            </label>
          )}
          <p className="text-xs text-slate-500">
            Suivi pédagogique interne. Ce bilan ne modifie pas le livret
            officiel RDVPermis.
          </p>
          <label className="block text-sm">
            Objectif du prochain cours
            <textarea
              required
              maxLength={1000}
              className="w-full rounded-lg border p-3"
              value={nextObjective}
              onChange={(e) => setNextObjective(e.target.value)}
            />
          </label>
          <label className="block text-sm">
            Action à signaler au secrétariat
            <textarea
              maxLength={1000}
              className="w-full rounded-lg border p-3"
              value={secretaryAlert}
              onChange={(e) => setSecretaryAlert(e.target.value)}
              placeholder="Uniquement les informations nécessaires au suivi administratif"
            />
          </label>
          <label className="block text-sm">
            Motif de l’avis examen
            <textarea
              required={readyForExam}
              maxLength={1000}
              className="w-full rounded-lg border p-3"
              value={examReason}
              onChange={(e) => setExamReason(e.target.value)}
            />
          </label>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">
              Synthèse de la séance
            </label>
            <textarea
              required
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              rows={4}
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 text-sm resize-none"
              placeholder="Points travaillés, progrès, axes d'amélioration…"
            />
          </div>

          <div>
            <span className="block text-sm font-medium text-slate-700 mb-2">
              Compétences travaillées
            </span>
            <div className="space-y-2">
              {SUGGESTED.map((label) => (
                <div
                  key={label}
                  className="flex items-center justify-between gap-3"
                >
                  <span className="text-sm text-slate-600">{label}</span>
                  <div className="flex gap-1.5">
                    {LEVELS.map((lvl) => (
                      <button
                        key={lvl.key}
                        type="button"
                        onClick={() => setLevel(label, lvl.key)}
                        className={`px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-colors ${
                          levelOf(label) === lvl.key
                            ? lvl.tone
                            : "bg-white text-slate-400 border-slate-200 hover:border-slate-300"
                        }`}
                      >
                        {lvl.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <span className="block text-sm font-medium text-slate-700 mb-2">
              Note de la séance
            </span>
            <div className="flex gap-1.5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  aria-label={`Note ${n}`}
                >
                  <Star
                    size={22}
                    className={
                      n <= rating
                        ? "text-amber-400 fill-amber-400"
                        : "text-slate-300"
                    }
                  />
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2.5 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={readyForExam}
              onChange={(e) => setReadyForExam(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-200"
            />
            Cet élève est prêt(e) pour l'examen
          </label>

          {mutation.isError && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {(mutation.error as Error).message}
            </div>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={close}
              className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm font-medium"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !summary.trim()}
              className="flex-1 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors"
            >
              {mutation.isPending ? "Enregistrement…" : "Enregistrer le bilan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
